import type { OTCDealData } from '@src/types/OTC';
import {
  ButtonVariants,
  NotificationVariants,
  abbreviateId,
  logger,
  request,
  useEndpointsContext,
  useGlobalDialog,
  useGlobalToasts,
} from '@talos/kyoko';
import { createElement, useCallback } from 'react';

import { CancelDealDialogContent } from './CancelDealDialogContent';

export function useCancelDealDialog() {
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useEndpointsContext();
  const { open } = useGlobalDialog();

  const handleCancelDeal = useCallback(
    (deal: OTCDealData) => {
      const dealID = deal.ID;
      if (dealID == null) {
        logger.error(new Error('Deal is null'), {
          extra: { dealID },
        });
        return;
      }

      open({
        title: 'Cancel Confirmation',
        width: 450,
        variant: ButtonVariants.Negative,
        confirmLabel: 'Cancel Deal',
        cancelLabel: 'Return',
        stretchButtons: false,
        content: createElement(CancelDealDialogContent, { deal }),
        onConfirm: async () => {
          try {
            await request('POST', `${orgApiEndpoint}/contracts/deals/${dealID}/close`);
            addToast({
              text: `Deal ${abbreviateId(dealID)} cancelled.`,
              variant: NotificationVariants.Positive,
            });
          } catch (e) {
            addToast({
              text: `Could not cancel trade: ${e instanceof Error ? e.message : 'Unknown error'}`,
              variant: NotificationVariants.Negative,
            });
          }
        },
      });
    },
    [addToast, orgApiEndpoint, open]
  );

  return handleCancelDeal;
}
